import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Divider,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EyeOutlined, FilePdfOutlined } from '@ant-design/icons';
import api from '../../../api/client';
import {
  buildFilasGrillaCotizaciones,
  titulosConceptosDesdeCotizaciones,
  renderCeldaAgrupada,
  renderColumnaNumeroConExpand,
} from '../cotizaciones/cotizacionListaUtils';

const { Title, Text, Paragraph } = Typography;

const LIMIT_LISTA = 25;

const CotizacionesEliminadas = () => {
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [tipo, setTipo] = useState('EXPORTACION');
  const [loadingLista, setLoadingLista] = useState(true);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [pageLista, setPageLista] = useState(1);
  const [totalLista, setTotalLista] = useState(0);
  const [searchLista, setSearchLista] = useState('');
  const [searchListaDebounced, setSearchListaDebounced] = useState('');

  const [modalVerOpen, setModalVerOpen] = useState(false);
  const [detalleVer, setDetalleVer] = useState(null);
  const [loadingVer, setLoadingVer] = useState(false);
  const [loadingPdfVer, setLoadingPdfVer] = useState(false);
  const [cotizacionesExpandidas, setCotizacionesExpandidas] = useState(() => new Set());

  const cargarLista = useCallback(async () => {
    setLoadingLista(true);
    try {
      const { data } = await api.get('/cotizaciones/eliminadas', {
        params: {
          page: pageLista,
          limit: LIMIT_LISTA,
          search: searchListaDebounced.trim() || undefined,
          tipoOperacion: tipo,
        },
      });
      setCotizaciones(data.items || []);
      setTotalLista(data.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar cotizaciones eliminadas');
    } finally {
      setLoadingLista(false);
    }
  }, [pageLista, searchListaDebounced, tipo]);

  useEffect(() => {
    const t = setTimeout(() => setSearchListaDebounced(searchLista.trim()), 350);
    return () => clearTimeout(t);
  }, [searchLista]);

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  const abrirVer = useCallback(async (row) => {
    setModalVerOpen(true);
    setDetalleVer(null);
    setLoadingVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${row.id}`);
      setDetalleVer(data);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar la cotizacion');
      setModalVerOpen(false);
    } finally {
      setLoadingVer(false);
    }
  }, []);

  const cerrarVer = useCallback(() => {
    setModalVerOpen(false);
    setDetalleVer(null);
  }, []);

  const descargarPdfVer = useCallback(async () => {
    if (!detalleVer?.id) return;
    setLoadingPdfVer(true);
    try {
      const { data } = await api.get(`/cotizaciones/${detalleVer.id}/resumen-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const nro = detalleVer.numeroSecuencial ?? detalleVer.id;
      const tag = detalleVer.tipoOperacion === 'IMPORTACION' ? 'impo' : 'expo';
      a.download = `cotizacion-${tag}-${nro}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'Error al generar el PDF';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await d.text();
          const j = JSON.parse(txt);
          if (j?.message) msg = j.message;
        } else if (typeof d?.message === 'string') {
          msg = d.message;
        }
      } catch {
        /* ignore */
      }
      message.error(msg);
    } finally {
      setLoadingPdfVer(false);
    }
  }, [detalleVer]);

  const guardarNumeroSecuencialLista = useCallback(
    async (row, nuevoNumero) => {
      try {
        await api.patch(`/cotizaciones/${row.id}/numero-secuencial`, {
          numeroSecuencial: nuevoNumero,
        });
        message.success(`Numero actualizado a ${nuevoNumero}`);
        cargarLista();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo actualizar el numero');
        cargarLista();
      }
    },
    [cargarLista]
  );

  const borrarDefinitivo = useCallback(
    async (row) => {
      try {
        await api.delete(`/cotizaciones/eliminadas/${row.id}`);
        message.success(`Cotizacion Nº ${row.numeroSecuencial ?? row.id} borrada definitivamente`);
        if (detalleVer?.id === row.id) {
          setModalVerOpen(false);
          setDetalleVer(null);
        }
        cargarLista();
      } catch (e) {
        message.error(e?.response?.data?.message || 'No se pudo borrar la cotizacion');
      }
    },
    [cargarLista, detalleVer]
  );

  const vaciarEliminadas = useCallback(() => {
    const label = tipo === 'IMPORTACION' ? 'importación' : 'exportación';
    Modal.confirm({
      title: 'Borrar definitivamente',
      content: `Se van a borrar para siempre todas las cotizaciones eliminadas de ${label}. Esta accion no se puede deshacer. ¿Continuar?`,
      okText: 'Borrar todas',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const { data } = await api.delete('/cotizaciones/eliminadas', {
            params: { tipoOperacion: tipo },
          });
          message.success(data.message || `Se borraron ${data.deleted ?? 0} cotizaciones`);
          setPageLista(1);
          cargarLista();
        } catch (e) {
          message.error(e?.response?.data?.message || 'No se pudo vaciar la papelera');
        }
      },
    });
  }, [tipo, cargarLista]);

  const toggleExpandCotizacion = useCallback((id) => {
    setCotizacionesExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filasLista = useMemo(
    () => buildFilasGrillaCotizaciones(cotizaciones, cotizacionesExpandidas),
    [cotizaciones, cotizacionesExpandidas]
  );

  const titulosConceptosGrillaLista = useMemo(
    () => titulosConceptosDesdeCotizaciones(cotizaciones),
    [cotizaciones]
  );

  const columnasLista = useMemo(() => {
    const fijas = [
      {
        title: 'Operación',
        key: 'tipoOp',
        width: 110,
        render: (_, row) =>
          renderCeldaAgrupada(
            row,
            row.tipoOperacion === 'IMPORTACION' ? (
              <Tag color="blue">Importación</Tag>
            ) : (
              <Tag color="green">Exportación</Tag>
            )
          ),
      },
      {
        title: 'Nº',
        dataIndex: 'numeroSecuencial',
        width: 110,
        responsive: ['sm'],
        render: (v, row) =>
          renderColumnaNumeroConExpand(v, row, toggleExpandCotizacion, guardarNumeroSecuencialLista),
      },
      {
        title: 'CUIT',
        dataIndex: 'cuit',
        width: 130,
        ellipsis: true,
        render: (v, row) => renderCeldaAgrupada(row, v || '—'),
      },
      {
        title: 'Nombre del cliente',
        dataIndex: 'clienteNombre',
        ellipsis: true,
        width: 160,
        render: (v, row) => renderCeldaAgrupada(row, v || '—'),
      },
      {
        title: 'Posicion arancelaria',
        dataIndex: 'posicionArancelaria',
        ellipsis: true,
        width: 160,
      },
      {
        title: 'Nombre del producto',
        dataIndex: 'productoNombre',
        ellipsis: true,
        minWidth: 180,
      },
    ];
    const colsConceptos = titulosConceptosGrillaLista.map((nombreConcepto) => ({
      title: nombreConcepto,
      key: `cg-${nombreConcepto}`,
      ellipsis: true,
      minWidth: 140,
      render: (_, row) => {
        const hit = (row.conceptosGrilla || []).find((l) => l.nombre === nombreConcepto);
        const d = hit?.detalle;
        return d != null && String(d).trim() ? String(d).trim() : '—';
      },
    }));
    const colAprobada = {
      title: 'Aprobada',
      dataIndex: 'aprobada',
      width: 100,
      render: (v, row) =>
        renderCeldaAgrupada(row, v ? <Tag color="green">Si</Tag> : <Tag>No</Tag>),
    };
    const colAcciones = {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 140,
      render: (_, row) =>
        renderCeldaAgrupada(
          row,
          <Space size={0}>
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void abrirVer(row)}>
              Ver
            </Button>
            <Popconfirm
              title="Borrar definitivamente?"
              description="Se elimina de la base y libera el Nº. No se puede deshacer."
              okText="Borrar"
              okType="danger"
              cancelText="Cancelar"
              onConfirm={() => void borrarDefinitivo(row)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} title="Borrar definitivamente" />
            </Popconfirm>
          </Space>
        ),
    };
    return [...fijas, ...colsConceptos, colAprobada, colAcciones];
  }, [titulosConceptosGrillaLista, toggleExpandCotizacion, guardarNumeroSecuencialLista, abrirVer, borrarDefinitivo]);

  const scrollXLista = useMemo(
    () => Math.max(1396, 830 + titulosConceptosGrillaLista.length * 140),
    [titulosConceptosGrillaLista.length]
  );

  return (
    <>
      <Card
        title={
          <Title level={5} style={{ margin: 0, color: '#198039' }}>
            Cotizaciones eliminadas
          </Title>
        }
        style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
        styles={{ body: { padding: isNarrow ? 12 : 16 } }}
        extra={
          <Space wrap size="middle" style={{ justifyContent: 'flex-end' }}>
            <Radio.Group
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value);
                setPageLista(1);
              }}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="EXPORTACION">Exportación</Radio.Button>
              <Radio.Button value="IMPORTACION">Importación</Radio.Button>
            </Radio.Group>
            <Input.Search
              allowClear
              placeholder="Buscar por Nº, cliente, CUIT, producto, conceptos..."
              value={searchLista}
              onChange={(e) => {
                setSearchLista(e.target.value);
                setPageLista(1);
              }}
              style={{ width: isNarrow ? '100%' : 320 }}
            />
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={totalLista === 0}
              onClick={vaciarEliminadas}
            >
              Vaciar {tipo === 'IMPORTACION' ? 'importación' : 'exportación'}
            </Button>
          </Space>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Cotizaciones eliminadas de las grillas. Acá podés borrarlas definitivamente (una o todas del tipo)
          para liberar los números y recuperar la correlatividad.
        </Text>
        <Table
          rowKey="key"
          loading={loadingLista}
          columns={columnasLista}
          dataSource={filasLista}
          onRow={(record) => ({
            style: record._esFilasProductoSecundario ? { background: '#fafafa' } : undefined,
          })}
          pagination={{
            current: pageLista,
            pageSize: LIMIT_LISTA,
            total: totalLista,
            showSizeChanger: false,
            onChange: (p) => setPageLista(p),
            showTotal: (t) => `${t} cotizaciones`,
            size: isNarrow ? 'small' : 'default',
            simple: isNarrow,
          }}
          scroll={{ x: scrollXLista }}
          size="small"
        />
      </Card>

      <Modal
        title={detalleVer ? `Cotizacion Nº ${detalleVer.numeroSecuencial ?? detalleVer.id}` : 'Detalle'}
        open={modalVerOpen}
        onCancel={cerrarVer}
        footer={[
          <Button
            key="pdf"
            icon={<FilePdfOutlined />}
            loading={loadingPdfVer}
            disabled={!detalleVer || loadingVer}
            onClick={() => void descargarPdfVer()}
          >
            PDF
          </Button>,
          <Button key="cerrar" type="primary" onClick={cerrarVer}>
            Cerrar
          </Button>,
        ]}
        width={isNarrow ? 'calc(100vw - 24px)' : 560}
        destroyOnClose
      >
        <Spin spinning={loadingVer}>
          {detalleVer && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Operación
                </Text>
                <Tag color={detalleVer.tipoOperacion === 'IMPORTACION' ? 'blue' : 'green'}>
                  {detalleVer.tipoOperacion === 'IMPORTACION' ? 'Importación' : 'Exportación'}
                </Tag>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Cliente
                </Text>
                <div>
                  {detalleVer.clienteNombre || '—'}
                  {!detalleVer.esClientePotencial && detalleVer.cuit ? ` · CUIT ${detalleVer.cuit}` : ''}
                </div>
                {detalleVer.esClientePotencial ? (
                  <Text type="secondary">Cliente potencial</Text>
                ) : (
                  <Text type="secondary">Nº cliente: {detalleVer.numeroCliente ?? '—'}</Text>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {(detalleVer.productos || []).length > 1 ? 'Productos' : 'Producto'}
                </Text>
                {(detalleVer.productos || []).length > 0 ? (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {(detalleVer.productos || []).map((p) => (
                      <div key={p.id ?? p.productoId}>
                        <Text strong>{p.productoNombre || '—'}</Text>
                        {p.posicionArancelaria ? (
                          <div>
                            <Text type="secondary">Posicion arancelaria: {p.posicionArancelaria}</Text>
                          </div>
                        ) : null}
                        {(p.conceptos || []).length > 0 ? (
                          <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 8 }}>
                            {(p.conceptos || []).map((c) => (
                              <div key={c.id}>
                                <Text type="secondary">{c.nombre || 'Concepto'}</Text>
                                <Paragraph style={{ marginBottom: 0, marginTop: 2, whiteSpace: 'pre-wrap' }}>
                                  {c.detalle != null && String(c.detalle).trim() ? c.detalle : '—'}
                                </Paragraph>
                              </div>
                            ))}
                          </Space>
                        ) : (
                          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                            Sin conceptos
                          </Text>
                        )}
                      </div>
                    ))}
                  </Space>
                ) : (
                  <>
                    <div>{detalleVer.productoNombre || '—'}</div>
                    {detalleVer.posicionArancelaria ? (
                      <Text type="secondary">Posicion arancelaria: {detalleVer.posicionArancelaria}</Text>
                    ) : null}
                  </>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  Observaciones
                </Text>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {detalleVer.observaciones != null && String(detalleVer.observaciones).trim()
                    ? detalleVer.observaciones
                    : '—'}
                </Paragraph>
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div>
                <Text strong style={{ marginRight: 8 }}>
                  Aprobada
                </Text>
                {detalleVer.aprobada ? <Tag color="green">Si</Tag> : <Tag>No</Tag>}
              </div>
            </Space>
          )}
        </Spin>
      </Modal>
    </>
  );
};

export default CotizacionesEliminadas;
